import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { referenceDataAPI, tagsAPI } from "../services/api";
import TemplateConfirmationModal from "../Modal/TemplateConfirmationModal";
import { useAuth } from "../context/AuthContext";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 650px;
  height: 680px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  flex: 1;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  right: 0;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const ContentSection = styled.div`
  padding: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagContainer = styled.div`
  position: relative;
`;

const TagInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;
  overflow: visible;
`;

const TagInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagSuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 70px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  margin-top: 4px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 9999;
`;

const TagSuggestionItem = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f3f4f6;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #e5e7eb;
  }
`;

const AddTagButton = styled.button`
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00b5b8;
  color: #00b5b8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TagRemoveButton = styled.button`
  background: white;
  border: 1px solid #00b5b8;
  cursor: pointer;
  color: #00b5b8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const GenderSection = styled.div`
  margin-bottom: 1rem;
`;

const GenderAgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
`;

const GenderColumn = styled.div`
  flex: 1;
`;

const AgeColumn = styled.div`
  flex: 1;
`;

const GenderOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const RadioInput = styled.input`
  width: 0.9rem;
  height: 0.9rem;
  accent-color: #1d87f0;

  &:checked {
    background-color: #1d87f0;
    border-color: #1d87f0;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
`;

const AgeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
`;

const AgeInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AgeInput = styled.input`
  width: 70px;
  padding: 0.4rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  text-align: center;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #1d87f0;
    box-shadow: 0 0 0 3px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const AgeSeparator = styled.span`
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
`;

const MultiSelectContainer = styled.div`
  position: relative;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`;

const MultiSelectWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MultiSelectButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  height: fit-content;

  &:hover {
    background-color: #0891b2;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.8rem;

  &:focus {
    outline: none;
  }
`;

const ParameterOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const CategoryHeader = styled.div`
  padding: 0.35rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #00b5b8;
  background-color: #f0fafa;
  border-top: 1px solid #e5e7eb;
  pointer-events: none;
  user-select: none;

  &:first-child {
    border-top: none;
  }
`;

const Checkbox = styled.input`
  width: 0.8rem;
  height: 0.8rem;
  accent-color: #00b5b8;
`;

const TableContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
  margin-top: 0.5rem;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background-color: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;

  &:nth-child(1) {
    width: 28%;
  } /* พารามิเตอร์ */
  &:nth-child(2) {
    width: 44%;
  } /* ค่าปกติ - ใหญ่กว่าเล็กน้อยเพราะมี 3 คอลัมน์ย่อย */
  &:nth-child(3) {
    width: 28%;
  } /* ค่าที่กำหนด */
`;

const TableCell = styled.td`
  padding: 0.5rem;
  font-size: 0.8rem;
  color: #374151;
  vertical-align: top;
  text-align: center;

  /* ลบ padding สำหรับ cell ที่มีตารางย่อย */
  &.no-padding {
    padding: 0;
  }
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
`;

const TableDualRangeContainer = styled.div`
  flex: 1;
  position: relative;
  height: 3px;
  min-width: 150px;
`;

const TableRangeTrack = styled.div`
  width: 100%;
  height: 3px;
  background: #e5e7eb;
  border-radius: 1.5px;
  position: relative;
  overflow: hidden;
`;

const TableRangeProgress = styled.div`
  height: 100%;
  background: #1d87f0;
  border-radius: 1.5px;
  position: absolute;
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  z-index: 1;
  pointer-events: none;
`;

const TableRangeInput = styled.input`
  position: absolute;
  width: 100%;
  height: 3px;
  background: transparent;
  pointer-events: none;
  appearance: none;
  outline: none;
  z-index: 3;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1d87f0;
    cursor: pointer;
    pointer-events: all;
    position: relative;
    margin-top: -3.5px;
    transform: translateY(-0.5px);
  }

  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1d87f0;
    cursor: pointer;
    border: none;
    pointer-events: all;
    margin-top: -3.5px;
  }
`;

const TableRangeDisplay = styled.div`
  font-size: 0.75rem;
  color: #374151;
  min-width: 70px;
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
`;

const NormalRangeBadge = styled.span`
  background: transparent;
  color: #374151;
  padding: 0;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: normal;
`;

const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  table-layout: auto;
  margin: 0;
`;

const SubTableHeader = styled.thead`
  background-color: transparent;
`;

const SubTableRow = styled.tr`
  border-bottom: none;
`;

const SubTableHeaderCell = styled.th`
  padding: 0.4rem 0.3rem;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  border-right: none;
  white-space: nowrap;

  &:nth-child(1) {
    width: 20%;
  } /* เพศ */
  &:nth-child(2) {
    width: 40%;
  } /* ช่วงอายุ */
  &:nth-child(3) {
    width: 40%;
  } /* ค่าปกติ */
`;

const SubTableCell = styled.td`
  padding: 0.4rem 0.3rem;
  font-size: 0.7rem;
  color: #374151;
  border-right: none;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: center;
`;

const RadioOptionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ParameterInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const ParameterInput = styled.input`
  width: 55px;
  padding: 0.25rem 0.3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.7rem;
  text-align: center;

  &::placeholder {
    color: #9ca3af;
    font-size: 0.65rem;
  }

  &:focus {
    outline: none;
    border-color: #1d87f0;
    box-shadow: 0 0 0 2px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const ParameterSeparator = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`;

const SaveButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #0891b2;
  }
`;

const CreateTemplateModal = ({ isOpen, onClose, onSave }) => {
  const { getUserFullName } = useAuth();
  const [templateName, setTemplateName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]); // tags ที่ผู้ใช้เลือกแล้ว
  const [availableTags, setAvailableTags] = useState([]); // tags ทั้งหมดที่มีในระบบ
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [selectedGender, setSelectedGender] = useState("male");
  const [ageRange, setAgeRange] = useState(["", ""]); // เริ่มต้นเป็นค่าว่าง ให้ user กรอกเอง
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [tempSelectedParameters, setTempSelectedParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [parameterRanges, setParameterRanges] = useState({});
  const [parameterRadioValues, setParameterRadioValues] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allParameters, setAllParameters] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTags([]);
      setTagInput("");
      setShowTagSuggestions(false);
      setCreatedBy(getUserFullName());
    }
  }, [isOpen, getUserFullName]);

  useEffect(() => {
    if (!isOpen) return;

    const loadTags = async () => {
      try {
        const response = await tagsAPI.getUsedTags();

        if (
          response.status === "success" &&
          response.data &&
          Array.isArray(response.data)
        ) {
          const tagNames = response.data.map((tag) => tag.tag_name);
          setAvailableTags(tagNames);
          }
      } catch (error) {
        console.error("✗ Error loading tags:", error);
        setAvailableTags([
          "ผู้สูงอายุ",
          "พันธุกรรม",
          "เบาหวาน",
          "ความดันโลหิตสูง",
        ]);
      }
    };

    loadTags();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const loadParameters = async () => {
      setLoading(true);
      try {
        let gender = null;
        if (selectedGender === "male") gender = "M";
        else if (selectedGender === "female") gender = "F";
        // both → null (ดึงทั้งหมด)

        const minAge = parseFloat(ageRange[0]) || 0;
        const maxAge = parseFloat(ageRange[1]) || 999;
        const avgAge = (minAge + maxAge) / 2;

        const response = await referenceDataAPI.getParametersList({
          gender: gender === "both" ? null : gender,
          age: avgAge,
          minAge: minAge,
          maxAge: maxAge,
        });

        if (
          response.status === "success" &&
          response.data &&
          Array.isArray(response.data)
        ) {
          const parameters = response.data.map((paramData) => {
            const normalRange = paramData.normalRange || [0, 100];

            return {
              name: paramData.name,
              unit: paramData.unit || "",
              normalRange: normalRange,
              normalValue: paramData.normalValue,
              category: paramData.category,
              allNormalRanges: paramData.allNormalRanges || [],
            };
          });

          setAllParameters(parameters);
        } else {
          setAllParameters([]);
        }
      } catch (error) {
        console.error("✗ Error loading parameters:", error);

        const mockParameters = [
          {
            name: "Albumin",
            unit: "g/dL",
            normalRange: [3.8, 5.4],
            normalValue: "3.8-5.4",
            category: "การทำงานของตับ",
          },
          {
            name: "WBC",
            unit: "x10³/µL",
            normalRange: [4.5, 13.5],
            normalValue: "4.5-13.5",
            category: "เม็ดเลือดขาว",
          },
          {
            name: "Creatinine",
            unit: "mg/dL",
            normalRange: [0.55, 1.02],
            normalValue: "0.55-1.02",
            category: "การทำงานของไต",
          },
          {
            name: "HbA1C",
            unit: "%",
            normalRange: [0, 5.7],
            normalValue: "<5.7",
            category: "น้ำตาลในเลือด",
          },
        ];

        setAllParameters(mockParameters);
      } finally {
        setLoading(false);
      }
    };

    loadParameters();
  }, [selectedGender, ageRange, isOpen]);

  // อัปเดตค่าปกติของพารามิเตอร์ที่เลือกไว้แล้ว เมื่อเพศ/อายุเปลี่ยน
  useEffect(() => {
    if (selectedParameters.length === 0 || allParameters.length === 0) return;

    const updatedSelectedParams = selectedParameters.map((selectedParam) => {
      const updatedParam = allParameters.find(
        (p) => p.name === selectedParam.name,
      );
      if (updatedParam) {
        return {
          ...selectedParam,
          normalRange: updatedParam.normalRange,
          normalValue: updatedParam.normalValue,
          unit: updatedParam.unit,
          allNormalRanges: updatedParam.allNormalRanges || [],
        };
      }
      return selectedParam;
    });

    // เช็คก่อน setState เพื่อหลีกเลี่ยง infinite loop
    const hasChanges = updatedSelectedParams.some((param, index) => {
      const oldParam = selectedParameters[index];
      const oldRangesLength = (oldParam.allNormalRanges || []).length;
      const newRangesLength = (param.allNormalRanges || []).length;

      return (
        param.normalValue !== oldParam.normalValue ||
        param.normalRange[0] !== oldParam.normalRange[0] ||
        param.normalRange[1] !== oldParam.normalRange[1] ||
        oldRangesLength !== newRangesLength
      );
    });

    if (hasChanges) {
      setSelectedParameters(updatedSelectedParams);
    }
  }, [allParameters]);

  const addTag = (tagName = null) => {
    const tagToAdd = tagName || tagInput.trim();
    if (tagToAdd && !tags.includes(tagToAdd)) {
      setTags([...tags, tagToAdd]);
      setTagInput("");
      setShowTagSuggestions(false);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const filteredTags = availableTags.filter(
    (tag) =>
      tag.toLowerCase().includes(tagInput.toLowerCase()) && !tags.includes(tag),
  );

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setTagInput(value);
    setShowTagSuggestions(value.trim().length > 0);
  };

  const handleMinAgeChange = (e) => {
    let value = e.target.value;
    if (value === "" || value === ".") {
      setAgeRange([value, ageRange[1]]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([roundedValue, ageRange[1]]);
    }
  };

  const handleMaxAgeChange = (e) => {
    let value = e.target.value;
    if (value === "" || value === ".") {
      setAgeRange([ageRange[0], value]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([ageRange[0], roundedValue]);
    }
  };

  // ลบ "_hide_" prefix ออกจากชื่อพารามิเตอร์สำหรับแสดงใน UI
  const cleanParameterName = (name) => {
    if (name && name.startsWith("_hide_")) {
      return name.substring(6);
    }
    return name;
  };

  const filteredParameters = allParameters.filter((param) =>
    param.normalValue !== null && param.normalValue !== '' &&
    param.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleParameterToggle = (parameter) => {
    const isSelected = tempSelectedParameters.some(
      (p) => p.name === parameter.name,
    );
    if (isSelected) {
      setTempSelectedParameters(
        tempSelectedParameters.filter((p) => p.name !== parameter.name),
      );
    } else {
      setTempSelectedParameters([...tempSelectedParameters, parameter]);
    }
  };

  const isRadioParameter = (parameter) => {
    const radioParams = [
      "Urine Appearance",
      "Color",
      "Clarity",
      "Color (Stool)",
      "Appearance (Stool)",
      "WBC (Stool)",
      "RBC (Stool)",
      "Parasite",
      "RBC Index",
    ];
    return radioParams.includes(parameter.name);
  };

  const isDropdownParameter = (parameter) => {
    const dropdownParams = [
      "Protein", "Sugar", "Glucose", "Ketones", "Blood",
      "Leukocytes", "Nitrite", "Bilirubin", "Urobilinogen", "Bacteria",
    ];
    return dropdownParams.includes(parameter.name);
  };

  const isNegPosParameter = (parameter) => {
    const negPosParams = [
      "HBsAb", "HBsAg", "HCV Ab", "Hemoglobin detection stool",
    ];
    return negPosParams.includes(parameter.name);
  };

  const isCountDropdownParameter = (parameter) => {
    const countParams = [
      "RBC (Urine)", "Red Blood Cells",
      "WBC (Urine)", "White Blood Cells",
      "Epithelial cell", "เซลล์เยื่อบุผิว",
    ];
    return countParams.includes(parameter.name);
  };

  const DROPDOWN_OPTIONS = ["Negative", "Trace", "1+", "2+", "3+", "4+"];
  const NEG_POS_OPTIONS = ["Negative", "Positive"];
  const COUNT_DROPDOWN_OPTIONS = ["0-1", "1-2", "2-3", "3-5", "5-10", "10-20", "20-30", "30-50", "50-100", "100-200", ">200"];

  const shouldHideGenderAgeTable = (parameter) => {
    const hideTableParams = [];
    return hideTableParams.includes(parameter.name);
  };

  const getRadioOptions = (parameterName) => {
    const optionsMap = {
      "Urine Appearance": ["Clear", "ผิดปกติ"],
      Color: ["เหลืองอ่อน", "ผิดปกติ"],
      Clarity: ["Clear", "ผิดปกติ"],
      "Color (Stool)": ["xxx"],
      "Appearance (Stool)": ["xxx"],
      "WBC (Stool)": ["Not found", "Found"],
      "RBC (Stool)": ["Not found", "Found"],
      Parasite: ["Not found", "Found"],
      "RBC Index": ["xxx"],
      eGFR: ["xxx"],
    };
    return optionsMap[parameterName] || ["ไม่มี", "มี"];
  };

  const handleSelectParameters = () => {
    setSelectedParameters([...tempSelectedParameters]);

    const newRanges = { ...parameterRanges };
    const newRadioValues = { ...parameterRadioValues };

    Object.keys(newRanges).forEach((paramName) => {
      if (!tempSelectedParameters.some((p) => p.name === paramName)) {
        delete newRanges[paramName];
      }
    });

    Object.keys(newRadioValues).forEach((paramName) => {
      if (!tempSelectedParameters.some((p) => p.name === paramName)) {
        delete newRadioValues[paramName];
      }
    });

    tempSelectedParameters.forEach((parameter) => {
      if (isRadioParameter(parameter)) {
        if (!newRadioValues[parameter.name]) {
          const options = getRadioOptions(parameter.name);
          newRadioValues[parameter.name] = options[0];
        }
      } else if (isNegPosParameter(parameter)) {
        if (!newRadioValues[parameter.name]) {
          newRadioValues[parameter.name] = "Negative";
        }
      } else if (isCountDropdownParameter(parameter)) {
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = ["0-1", "0-1"];
        }
      } else if (isDropdownParameter(parameter)) {
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = ["Negative", "Negative"];
        }
      } else {
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = ["", ""];
        }
      }
    });

    setParameterRanges(newRanges);
    setParameterRadioValues(newRadioValues);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const handleDropdownOpen = () => {
    setTempSelectedParameters([...selectedParameters]);
    setIsDropdownOpen(true);
  };

  // สร้าง placeholder จาก normalValue
  const getPlaceholderFromNormalValue = (normalValue) => {
    if (!normalValue) return ["", ""];

    const trimmed = normalValue.trim();

    if (trimmed.startsWith(">")) {
      const val = trimmed.substring(1);
      return ["0", val];
    } else if (trimmed.startsWith("<")) {
      const val = trimmed.substring(1);
      return ["0", val];
    } else if (trimmed.includes("-")) {
      const parts = trimmed.split("-");
      return parts.length === 2 ? [parts[0], parts[1]] : ["", ""];
    } else {
      return [trimmed, trimmed];
    }
  };

  const getCustomPlaceholder = (paramName) => {
    const group1Params = [
      "Protein",
      "Sugar",
      "Glucose",
      "Ketones",
      "Blood",
      "Leukocytes",
      "Nitrite",
      "Bilirubin",
      "Urobilinogen",
      "Bacteria",
    ];

    const group2Params = [
      "Red Blood Cells",
      "White Blood Cells",
      "RBC (Urine)",
      "WBC (Urine)",
      "เซลล์เยื่อบุผิว",
      "Epithelial cell",
    ];

    if (group1Params.includes(paramName)) {
      return ["0", "4+"];
    }

    if (group2Params.includes(paramName)) {
      return ["0", ">200"];
    }

    return null;
  };

  const handleParameterRangeChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || [0, 100];

    if (value === "" || value === ".") {
      const newRange = isMin
        ? [value, currentRange[1]]
        : [currentRange[0], value];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange,
      });
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const newRange = isMin
        ? [numValue, currentRange[1]]
        : [currentRange[0], numValue];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange,
      });
    }
  };

  const handleRadioChange = (paramName, value) => {
    setParameterRadioValues({
      ...parameterRadioValues,
      [paramName]: value,
    });
  };

  const handleDropdownChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || ["Negative", "Negative"];
    const newRange = isMin ? [value, currentRange[1]] : [currentRange[0], value];
    setParameterRanges({ ...parameterRanges, [paramName]: newRange });
  };

  const handleSave = () => {
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    const newTemplate = {
      templateName,
      createdBy,
      tags,
      gender: selectedGender,
      ageRange,
      selectedParameters,
      parameterRanges,
      parameterRadioValues,
    };
    onSave(newTemplate);
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>สร้าง Template</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentSection>
            <FormGroup>
              <Label>ชื่อ Template</Label>
              <Input
                type="text"
                placeholder="ชื่อ Template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ชื่อผู้สร้าง</Label>
              <Input
                type="text"
                placeholder="ชื่อผู้สร้าง"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                readOnly
                style={{ backgroundColor: "#F9FAFB", cursor: "not-allowed" }}
              />
            </FormGroup>

            <FormGroup>
              <Label>ป้ายกำกับ (Tag)</Label>
              <TagContainer>
                <TagInputContainer>
                  <TagInput
                    type="text"
                    placeholder="พิมพ์เพื่อค้นหา tag..."
                    value={tagInput}
                    onChange={handleTagInputChange}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    onFocus={() =>
                      setShowTagSuggestions(tagInput.trim().length > 0)
                    }
                    onBlur={() =>
                      setTimeout(() => setShowTagSuggestions(false), 200)
                    }
                  />
                  <AddTagButton onClick={() => addTag()}>เพิ่ม</AddTagButton>

                  {showTagSuggestions && filteredTags.length > 0 && (
                    <TagSuggestionsDropdown>
                      {filteredTags.map((tag, index) => (
                        <TagSuggestionItem
                          key={index}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addTag(tag);
                          }}
                        >
                          {tag}
                        </TagSuggestionItem>
                      ))}
                    </TagSuggestionsDropdown>
                  )}
                </TagInputContainer>

                {tags.length > 0 && (
                  <TagList>
                    {tags.map((tag, index) => (
                      <Tag key={index}>
                        <span>{tag}</span>
                        <TagRemoveButton onClick={() => removeTag(tag)}>
                          <X size={8} />
                        </TagRemoveButton>
                      </Tag>
                    ))}
                  </TagList>
                )}
              </TagContainer>
            </FormGroup>

            <GenderSection>
              <GenderAgeRow>
                <GenderColumn>
                  <Label>สำหรับเพศ</Label>
                  <GenderOptions>
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={selectedGender === "male"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="male">ชาย</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={selectedGender === "female"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="female">หญิง</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="both"
                        name="gender"
                        value="both"
                        checked={selectedGender === "both"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="both">ทุกเพศ</RadioLabel>
                    </RadioContainer>
                  </GenderOptions>
                </GenderColumn>

                <AgeColumn>
                  <Label>ช่วงอายุ (ปี)</Label>
                  <AgeRangeContainer>
                    <AgeInputWrapper>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="0.000"
                        value={ageRange[0]}
                        onChange={handleMinAgeChange}
                      />
                      <AgeSeparator>-</AgeSeparator>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="999"
                        value={ageRange[1]}
                        onChange={handleMaxAgeChange}
                      />
                    </AgeInputWrapper>
                  </AgeRangeContainer>
                </AgeColumn>
              </GenderAgeRow>
            </GenderSection>

            <FormGroup>
              <Label>เลือกพารามิเตอร์</Label>
              <MultiSelectContainer>
                <MultiSelectWrapper>
                  <MultiSelectButton type="button" onClick={handleDropdownOpen}>
                    <span>
                      {selectedParameters.length === 0
                        ? "เลือกพารามิเตอร์"
                        : `${selectedParameters.length} selected`}
                    </span>
                    <span>{isDropdownOpen ? "▲" : "▼"}</span>
                  </MultiSelectButton>

                  {isDropdownOpen && (
                    <DropdownContent>
                      <SearchInput
                        type="text"
                        placeholder="ค้นหาพารามิเตอร์..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {loading ? (
                        <ParameterOption
                          style={{ justifyContent: "center", color: "#6B7280" }}
                        >
                          กำลังโหลดพารามิเตอร์...
                        </ParameterOption>
                      ) : filteredParameters.length === 0 ? (
                        <ParameterOption
                          style={{ justifyContent: "center", color: "#6B7280" }}
                        >
                          {searchTerm
                            ? "ไม่พบพารามิเตอร์ที่ค้นหา"
                            : "ไม่มีพารามิเตอร์"}
                        </ParameterOption>
                      ) : (
                        (() => {
                          const groups = [];
                          const seen = new Map();
                          filteredParameters.forEach((param) => {
                            const cat = param.category || 'อื่นๆ';
                            if (!seen.has(cat)) {
                              seen.set(cat, []);
                              groups.push({ category: cat, params: seen.get(cat) });
                            }
                            seen.get(cat).push(param);
                          });
                          return groups.map(({ category, params }) => (
                            <React.Fragment key={category}>
                              <CategoryHeader>{category}</CategoryHeader>
                              {params.map((parameter, index) => (
                                <ParameterOption
                                  key={index}
                                  onClick={() => handleParameterToggle(parameter)}
                                >
                                  <Checkbox
                                    type="checkbox"
                                    checked={tempSelectedParameters.some(
                                      (p) => p.name === parameter.name,
                                    )}
                                    onChange={() => {}}
                                  />
                                  <span>
                                    {cleanParameterName(parameter.name)} (
                                    {parameter.unit})
                                  </span>
                                </ParameterOption>
                              ))}
                            </React.Fragment>
                          ));
                        })()
                      )}
                    </DropdownContent>
                  )}
                </MultiSelectWrapper>

                <SelectButton onClick={handleSelectParameters}>
                  เลือก
                </SelectButton>
              </MultiSelectContainer>
            </FormGroup>

            <TableContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>พารามิเตอร์</TableHeaderCell>
                    <TableHeaderCell>ค่าปกติ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่กำหนด</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {selectedParameters.length > 0 ? (
                    selectedParameters.map((param, index) => {
                      const isRadio = isRadioParameter(param);
                      const isNegPos = isNegPosParameter(param);
                      const isCountDrop = isCountDropdownParameter(param);

                      if (isNegPos) {
                        const negPosValue = parameterRadioValues[param.name] || "Negative";
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);

                        return (
                          <TableRow key={index}>
                            <TableCell>{cleanParameterName(param.name)}</TableCell>
                            <TableCell className={allNormalRanges.length > 0 && !hideGenderAge ? "no-padding" : ""}>
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ช่วงอายุ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>{range.age_range}</SubTableCell>
                                        <SubTableCell>{range.normal_range}</SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>{param.normalValue ?? "null"}</NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              <select
                                value={negPosValue}
                                onChange={(e) =>
                                  handleRadioChange(param.name, e.target.value)
                                }
                                style={{ padding: "0.25rem 0.5rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.8rem", cursor: "pointer" }}
                              >
                                {NEG_POS_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      if (isCountDrop) {
                        const countRange = parameterRanges[param.name] || ["0-1", "0-1"];
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);

                        return (
                          <TableRow key={index}>
                            <TableCell>{cleanParameterName(param.name)}</TableCell>
                            <TableCell className={allNormalRanges.length > 0 && !hideGenderAge ? "no-padding" : ""}>
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>เพศ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ช่วงอายุ</SubTableHeaderCell>
                                      <SubTableHeaderCell>ค่าปกติ</SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>{range.age_range}</SubTableCell>
                                        <SubTableCell>{range.normal_range}</SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>{param.normalValue ?? "null"}</NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              <ParameterInputWrapper>
                                <select
                                  value={countRange[0] || "0-1"}
                                  onChange={(e) => handleDropdownChange(param.name, true, e.target.value)}
                                  style={{ width: "65px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                >
                                  {COUNT_DROPDOWN_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                                <ParameterSeparator>-</ParameterSeparator>
                                <select
                                  value={countRange[1] || "0-1"}
                                  onChange={(e) => handleDropdownChange(param.name, false, e.target.value)}
                                  style={{ width: "65px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                >
                                  {COUNT_DROPDOWN_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              </ParameterInputWrapper>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      if (isRadio) {
                        const options = getRadioOptions(param.name);
                        const radioValue =
                          parameterRadioValues[param.name] || options[0];
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              {cleanParameterName(param.name)}
                            </TableCell>
                            <TableCell
                              className={
                                allNormalRanges.length > 0 && !hideGenderAge
                                  ? "no-padding"
                                  : ""
                              }
                            >
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>
                                        เพศ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ช่วงอายุ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ค่าปกติ
                                      </SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>
                                          {range.age_range}
                                        </SubTableCell>
                                        <SubTableCell>
                                          {range.normal_range}
                                        </SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>
                                  {param.normalValue ?? "null"}
                                </NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              <RadioOptionsContainer>
                                {options.map((option, optionIndex) => (
                                  <RadioContainer key={optionIndex}>
                                    <RadioInput
                                      type="radio"
                                      id={`${param.name}-${optionIndex}`}
                                      name={param.name}
                                      value={option}
                                      checked={radioValue === option}
                                      onChange={(e) =>
                                        handleRadioChange(
                                          param.name,
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <RadioLabel
                                      htmlFor={`${param.name}-${optionIndex}`}
                                    >
                                      {option}
                                    </RadioLabel>
                                  </RadioContainer>
                                ))}
                              </RadioOptionsContainer>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        const range = parameterRanges[param.name] || ["", ""];
                        const customPlaceholder = getCustomPlaceholder(
                          param.name,
                        );
                        const [minPlaceholder, maxPlaceholder] =
                          customPlaceholder ||
                          getPlaceholderFromNormalValue(param.normalValue);
                        const allNormalRanges = param.allNormalRanges || [];
                        const hideGenderAge = shouldHideGenderAgeTable(param);
                        const isDropdown = isDropdownParameter(param);

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              {cleanParameterName(param.name)}
                            </TableCell>
                            <TableCell
                              className={
                                allNormalRanges.length > 0 && !hideGenderAge
                                  ? "no-padding"
                                  : ""
                              }
                            >
                              {allNormalRanges.length > 0 && !hideGenderAge ? (
                                <SubTable>
                                  <SubTableHeader>
                                    <SubTableRow>
                                      <SubTableHeaderCell>
                                        เพศ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ช่วงอายุ
                                      </SubTableHeaderCell>
                                      <SubTableHeaderCell>
                                        ค่าปกติ
                                      </SubTableHeaderCell>
                                    </SubTableRow>
                                  </SubTableHeader>
                                  <tbody>
                                    {allNormalRanges.map((range, idx) => (
                                      <SubTableRow key={idx}>
                                        <SubTableCell>{range.sex}</SubTableCell>
                                        <SubTableCell>
                                          {range.age_range}
                                        </SubTableCell>
                                        <SubTableCell>
                                          {range.normal_range}
                                        </SubTableCell>
                                      </SubTableRow>
                                    ))}
                                  </tbody>
                                </SubTable>
                              ) : (
                                <NormalRangeBadge>
                                  {param.normalValue ?? "null"}
                                </NormalRangeBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              {isDropdown ? (
                                <ParameterInputWrapper>
                                  <select
                                    value={range[0] || "Negative"}
                                    onChange={(e) => handleDropdownChange(param.name, true, e.target.value)}
                                    style={{ width: "65px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                  >
                                    {DROPDOWN_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                  </select>
                                  <ParameterSeparator>-</ParameterSeparator>
                                  <select
                                    value={range[1] || "Negative"}
                                    onChange={(e) => handleDropdownChange(param.name, false, e.target.value)}
                                    style={{ width: "65px", padding: "0.25rem 0.2rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.7rem", cursor: "pointer" }}
                                  >
                                    {DROPDOWN_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                  </select>
                                </ParameterInputWrapper>
                              ) : (
                                <ParameterInputWrapper>
                                  <ParameterInput
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={minPlaceholder}
                                    value={range[0]}
                                    onChange={(e) =>
                                      handleParameterRangeChange(
                                        param.name,
                                        true,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <ParameterSeparator>-</ParameterSeparator>
                                  <ParameterInput
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={maxPlaceholder}
                                    value={range[1]}
                                    onChange={(e) =>
                                      handleParameterRangeChange(
                                        param.name,
                                        false,
                                        e.target.value,
                                      )
                                    }
                                  />
                                </ParameterInputWrapper>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    })
                  ) : (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </ContentSection>

          <ActionSection>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SaveButton onClick={handleSave}>สร้าง Template</SaveButton>
          </ActionSection>
        </ModalBody>
      </ModalContainer>

      <TemplateConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmSave}
      />
    </ModalOverlay>
  );
};

export default CreateTemplateModal;
